import { createSelector } from 'reselect';
import { PayInMethodCode } from 'constants/pay-in-method';
import { createDeepEqualSelector } from 'state/selectors';
import { stateName } from './constants';

const localState = state => state[stateName];

const getExtraServiceOrderPayment = state => localState(state).orderPayment;

export const getExtraServiceErrorMessage = state => localState(state).errorMessage;
export const getExtraServicePayInMethods = state => localState(state).payInMethods;
export const getCreditCards = state => getExtraServicePayInMethods(state).creditCards;
export const getPayInMethods = state => getExtraServicePayInMethods(state).payInMethods;
export const getWalletBalance = state => getExtraServicePayInMethods(state).walletBalance;
export const getPayInMethodsUiState = state => getExtraServicePayInMethods(state).uiState;
export const getSelectedPayInMethodId = state => {
  var _getExtraServicePayIn;

  return (_getExtraServicePayIn = getExtraServicePayInMethods(state).selectedPayInMethod) === null || _getExtraServicePayIn === void 0 ? void 0 : _getExtraServicePayIn.id;
};
export const getSelectedCreditCardId = state => {
  var _getExtraServicePayIn2;

  return (_getExtraServicePayIn2 = getExtraServicePayInMethods(state).selectedPayInMethod) === null || _getExtraServicePayIn2 === void 0 ? void 0 : _getExtraServicePayIn2.creditCardId;
};
export const getPaymentAuth = createSelector(getExtraServiceOrderPayment, _ref => {
  let {
    auth
  } = _ref;
  return auth;
});
export const getPaymentStatus = createSelector(getExtraServiceOrderPayment, _ref2 => {
  let {
    status
  } = _ref2;
  return status;
});
export const getPaymentErrorMessage = createSelector(getExtraServiceOrderPayment, _ref3 => {
  let {
    error
  } = _ref3;
  return error.message;
});
export const getPermanentCreditCards = createSelector(getCreditCards, creditCards => creditCards.filter(_ref4 => {
  let {
    isSingleUse
  } = _ref4;
  return !isSingleUse;
}));
export const getIsWalletBalanceInsufficient = createDeepEqualSelector([getWalletBalance, (state, payableAmount) => payableAmount], (walletBalance, payableAmount) => {
  if (!walletBalance || !payableAmount) return false;
  const orderPayableAmount = parseFloat(payableAmount);
  const walletBalanceAmount = parseFloat(walletBalance.availableAmount.amount);
  return orderPayableAmount > walletBalanceAmount;
});
export const getPreferredPayInMethod = createSelector(getPayInMethods, payInMethods => payInMethods.find(_ref5 => {
  let {
    preferred
  } = _ref5;
  return !!preferred;
}));
export const getSelectedPayInMethod = createSelector(getPayInMethods, getSelectedPayInMethodId, (payInMethods, selectedMethodId) => payInMethods.find(_ref6 => {
  let {
    id
  } = _ref6;
  return id === selectedMethodId;
}));
export const getSelectedCreditCard = createSelector(getCreditCards, getSelectedCreditCardId, getSelectedPayInMethod, getPreferredPayInMethod, (creditCards, selectedCreditCardId, selectedPayInMethod, preferredPayInMethod) => {
  const activePayInMethod = selectedPayInMethod || preferredPayInMethod;
  if ((activePayInMethod === null || activePayInMethod === void 0 ? void 0 : activePayInMethod.code) !== PayInMethodCode.CreditCard) return null;

  if (selectedCreditCardId) {
    return creditCards.find(_ref7 => {
      let {
        id
      } = _ref7;
      return id === selectedCreditCardId;
    });
  }

  return creditCards.find(_ref8 => {
    let {
      isDefault
    } = _ref8;
    return isDefault;
  });
});
export const getActivePayInMethod = createSelector(getSelectedCreditCard, getSelectedPayInMethod, getPreferredPayInMethod, getIsWalletBalanceInsufficient, (selectedCreditCard, selectedPayInMethod, preferredPayInMethod, isWalletBalanceInsufficient) => {
  const activeMethod = selectedPayInMethod || preferredPayInMethod;
  if ((activeMethod === null || activeMethod === void 0 ? void 0 : activeMethod.code) === PayInMethodCode.CreditCard && !selectedCreditCard) return null;
  if ((activeMethod === null || activeMethod === void 0 ? void 0 : activeMethod.code) === PayInMethodCode.Wallet && isWalletBalanceInsufficient) return null;
  if ((activeMethod === null || activeMethod === void 0 ? void 0 : activeMethod.code) === PayInMethodCode.ApplePay) return null;
  if ((activeMethod === null || activeMethod === void 0 ? void 0 : activeMethod.code) === PayInMethodCode.GooglePay) return null;
  return activeMethod !== null && activeMethod !== void 0 ? activeMethod : null;
});
export const getCreditCardPayInMethod = createSelector(getPayInMethods, payInMethods => payInMethods.find(_ref9 => {
  let {
    code
  } = _ref9;
  return code === PayInMethodCode.CreditCard;
}));
export const getIsBlikAuthRequired = createDeepEqualSelector([getActivePayInMethod, (state, payableAmount) => payableAmount], (activePayInMethod, payableAmount) => {
  if ((activePayInMethod === null || activePayInMethod === void 0 ? void 0 : activePayInMethod.code) !== PayInMethodCode.BlikDirect) return false;
  if (!payableAmount) return true;
  const orderPayableAmount = parseFloat(payableAmount);
  return orderPayableAmount > 0;
});
export const getConfiguration = createSelector(getExtraServiceOrderPayment, _ref10 => {
  let {
    configuration
  } = _ref10;
  return configuration;
});
export const getThreeDS2Configuration = createSelector(getConfiguration, configuration => (configuration === null || configuration === void 0 ? void 0 : configuration.threeDS2) || null);